import React from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  Typography,
  Box,
  Chip,
  Avatar,
  Divider,
  useTheme,
  useMediaQuery,
  IconButton,
} from '@mui/material';
import {
  Close,
  CalendarToday,
  AccessTime,
  Person,
  VideoCall,
  Description,
  Medication,
  OpenInNew,
} from '@mui/icons-material';
import colors from '../../../shared-colors';
import StatusIndicator from './StatusIndicator';
import PrescriptionCard from './PrescriptionCard';
import { useTranslation } from 'react-i18next';

const VisitDetailsModal = ({ open, onClose, visit, formatDate, formatTime, getVisitTypeLabel }) => {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('sm'));
  const { t } = useTranslation();

  const getStatusColor = (status) => {
    switch (status) {
      case 'PENDING':
        return '#FF9800'; // Orange for pending
      case 'COMPLETED':
        return '#4CAF50'; // Green for completed
      case 'CANCELLED':
        return '#f44336'; // Red for cancelled
      default:
        return colors.grayText;
    }
  };

  if (!visit) return null;

  const handleJoinMeeting = () => {
    if (visit.meetingUrl) {
      window.open(visit.meetingUrl, '_blank');
    }
  };

  return (
    <Dialog
      open={open}
      onClose={onClose}
      maxWidth="md"
      fullWidth
      fullScreen={isMobile}
      PaperProps={{
        sx: {
          borderRadius: isMobile ? 0 : '12px',
          maxHeight: isMobile ? '100%' : '90vh',
        },
      }}
    >
      <DialogTitle sx={{ pb: 1 }}>
        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
          <Typography variant="h6" sx={{ fontWeight: 600, color: colors.black }}>
            {t('visitDetails')}
          </Typography>
          <IconButton onClick={onClose} size="small">
            <Close />
          </IconButton>
        </Box>
      </DialogTitle>

      <DialogContent sx={{ pb: 2 }}>
        <Box sx={{ display: 'flex', flexDirection: 'column', gap: 3 }}>
          {/* Header Section */}
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, mb: 2 }}>
            <Avatar
              sx={{
                width: 64,
                height: 64,
                bgcolor: colors.mainBlue,
                fontSize: '24px',
                fontWeight: 600,
              }}
            >
              {visit.doctorDetails?.userInfo?.firstName?.charAt(0) || 'D'}
            </Avatar>
            <Box sx={{ flex: 1 }}>
              <Typography variant="h5" sx={{ fontWeight: 600, color: colors.black, mb: 0.5 }}>
                {visit.doctorDetails?.userInfo?.firstName} {visit.doctorDetails?.userInfo?.lastName}
              </Typography>
              <Typography variant="body1" sx={{ color: colors.grayText, mb: 1 }}>
                {visit.eventDetails?.title}
              </Typography>
              <Box sx={{ display: 'flex', alignItems: 'center', mb: 1 }}>
                <Typography variant="subtitle2" sx={{ fontWeight: '600', color: '#333', mr: 1 }}>
                  {t('status')}:
                </Typography>
                <Box
                  sx={{
                    width: '8px',
                    height: '8px',
                    borderRadius: '50%',
                    backgroundColor: getStatusColor(visit.status),
                    mr: 0.75,
                  }}
                />
                <Typography
                  variant="body2"
                  sx={{
                    color: getStatusColor(visit.status),
                    fontSize: '0.875rem',
                    fontWeight: 500,
                    textTransform: 'capitalize',
                  }}
                >
                  {visit.status}
                </Typography>
              </Box>
            </Box>
          </Box>

          {/* Visit Information */}
          <Box>
            <Typography variant="h6" sx={{ fontWeight: 600, color: colors.black, mb: 2 }}>
              {t('visitInformation')}
            </Typography>
            <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
                <CalendarToday sx={{ color: colors.grayText }} />
                <Typography variant="body1" sx={{ color: colors.black }}>
                  {formatDate(visit.scheduledStart)}
                </Typography>
              </Box>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
                <AccessTime sx={{ color: colors.grayText }} />
                <Typography variant="body1" sx={{ color: colors.black }}>
                  {formatTime(visit.scheduledStart)} - {formatTime(visit.scheduledEnd)}
                </Typography>
              </Box>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
                <Description sx={{ color: colors.grayText }} />
                <Typography variant="body1" sx={{ color: colors.black }}>
                  {getVisitTypeLabel(visit.visitType)}
                </Typography>
              </Box>
              {visit.eventDetails?.description && (
                <Box sx={{ mt: 2 }}>
                  <Typography variant="body2" sx={{ color: colors.grayText, mb: 1 }}>
                    {t('description')}:
                  </Typography>
                  <Typography variant="body1" sx={{ color: colors.black }}>
                    {visit.eventDetails.description}
                  </Typography>
                </Box>
              )}
            </Box>
          </Box>

          {/* Symptoms */}
          {visit.symptoms && visit.symptoms.length > 0 && (
            <Box>
              <Typography variant="h6" sx={{ fontWeight: 600, color: colors.black, mb: 2 }}>
                {t('symptoms')}
              </Typography>
              <Box sx={{ display: 'flex', gap: 1, flexWrap: 'wrap' }}>
                {visit.symptoms.map((symptom, index) => (
                  <Chip
                    key={index}
                    label={symptom}
                    sx={{
                      backgroundColor: colors.lightBlue,
                      color: colors.mainBlue,
                      fontWeight: 500,
                    }}
                  />
                ))}
              </Box>
            </Box>
          )}

          {/* Diagnosis */}
          {visit.diagnosis && (
            <Box>
              <Typography variant="h6" sx={{ fontWeight: 600, color: colors.black, mb: 2 }}>
                {t('diagnosis')}
              </Typography>
              <Typography variant="body1" sx={{ color: colors.black }}>
                {visit.diagnosis}
              </Typography>
            </Box>
          )}

          {/* Prescriptions */}
          {visit.prescriptions && visit.prescriptions.length > 0 && (
            <Box>
              <Typography variant="h6" sx={{ fontWeight: 600, color: colors.black, mb: 2 }}>
                {t('prescriptions')}
              </Typography>
              <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
                {visit.prescriptions.map((prescription, index) => (
                  <PrescriptionCard key={index} prescription={prescription} />
                ))}
              </Box>
            </Box>
          )}

          {/* AI Summary */}
          {visit.aiSummary?.visitSummary && (
            <Box>
              <Typography variant="h6" sx={{ fontWeight: 600, color: colors.black, mb: 2 }}>
                {t('visitSummary')}
              </Typography>
              <Typography variant="body1" sx={{ color: colors.black, lineHeight: 1.6 }}>
                {visit.aiSummary.visitSummary}
              </Typography>
            </Box>
          )}

          {/* Doctor Notes */}
          {visit.aiSummary?.doctorNotes && (
            <Box>
              <Typography variant="h6" sx={{ fontWeight: 600, color: colors.black, mb: 2 }}>
                {t('doctorNotes')}
              </Typography>
              <Typography variant="body1" sx={{ color: colors.black, lineHeight: 1.6 }}>
                {visit.aiSummary.doctorNotes}
              </Typography>
            </Box>
          )}
        </Box>
      </DialogContent>

      <DialogActions sx={{ p: 3, pt: 0 }}>
        <Button onClick={onClose} sx={{ color: colors.grayText }}>
          {t('close')}
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default VisitDetailsModal;
